import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Loader2, Minus, Plus } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { goodsCheckout, type CreatorProduct } from '@/services/creatorApi';
import { trackInitiateCheckout } from '@/lib/marketing';

// The page for a Product (productType 'goods'): photos, price, a size or option
// picker, quantity, and checkout. Physical orders give a US shipping address on
// Stripe's page; digital orders download right after paying. Signed out is fine:
// an email is enough.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const imgSrc = (k?: string | null) => !k ? '' : (k.startsWith('http') || k.startsWith('/api/') ? k : `/api/s3/${k}`);
const money = (c: number) => `$${(c / 100).toFixed(c % 100 ? 2 : 0)}`;

const GoodsProductView: React.FC<{ product: CreatorProduct; creator?: { displayName?: string; slug?: string } | null; previewOnly?: boolean }> = ({ product, creator, previewOnly }) => {
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();
  const g = product.goods!;
  const photos = (product.galleryImages && product.galleryImages.length ? product.galleryImages : [product.coverImage]).filter(Boolean) as string[];
  const [shown, setShown] = useState(0);
  const hasOptions = !!(g.options && g.options.length);
  const firstInStock = hasOptions ? (g.options.find((o) => o.stock > 0)?.key || '') : '';
  const [option, setOption] = useState(firstInStock);
  const [qty, setQty] = useState(1);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const params = new URLSearchParams(location.search);
  const purchased = params.get('purchased') === 'success' && params.get('goods') === '1';

  const left = hasOptions ? (g.options.find((o) => o.key === option)?.stock ?? 0) : (g.stock === null || g.stock === undefined ? null : g.stock);
  const soldOut = hasOptions ? g.options.every((o) => o.stock <= 0) : left === 0;
  const maxQty = Math.min(10, left === null ? 10 : Math.max(1, left));
  const physical = g.kind === 'physical';

  const buy = async () => {
    setErr(null);
    if (hasOptions && !option) { setErr(`Pick a ${(g.optionName || 'option').toLowerCase()}.`); return; }
    if (!isAuthenticated && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setErr('Enter your email address.'); return; }
    setBusy(true);
    try {
      trackInitiateCheckout(product);
      const r = await goodsCheckout(product._id, { option: option || undefined, quantity: qty, ...(isAuthenticated ? {} : { email: email.trim() }) });
      window.location.href = r.checkoutUrl;
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };

  return (
    <main className="mx-auto" style={{ maxWidth: 1040, padding: '0 16px 96px', background: 'var(--hj-bg)', color: INK, minHeight: 'calc(100vh - 68px)' }}>
      <div style={{ padding: '20px 0 12px' }}>
        {creator?.slug ? (
          <Link to={`/creators/${creator.slug}`} className="inline-flex items-center gap-1.5" style={{ color: INK, textDecoration: 'none', fontSize: 15, minHeight: 44 }}><ChevronLeft size={16} /> {creator.displayName || 'Store'}</Link>
        ) : <Link to="/marketplace" className="inline-flex items-center gap-1.5" style={{ color: INK, textDecoration: 'none', fontSize: 15, minHeight: 44 }}><ChevronLeft size={16} /> Marketplace</Link>}
      </div>

      {purchased && (
        <div style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
          <p className="m-0" style={{ fontWeight: 700, fontSize: 16 }}>Thanks, your order is in.</p>
          <p className="m-0" style={{ marginTop: 4, fontSize: 15 }}>
            {physical ? 'A confirmation is on its way to your email, and you’ll get another when it ships.' : <>Your download is ready. <Link to={`/purchases/${product._id}`} style={{ color: 'inherit', fontWeight: 700 }}>Open it here</Link>; the link is in your email too.</>}
          </p>
        </div>
      )}
      {previewOnly && <p style={{ background: 'var(--hj-yellow-tint)', border: '1px solid var(--hj-yellow)', borderRadius: 12, padding: 12, fontSize: 14, margin: '0 0 16px' }}>This is a draft preview. It isn’t for sale until it’s published.</p>}

      <div className="flex flex-wrap" style={{ gap: 32, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 380px', minWidth: 0 }}>
          {photos.length > 0 && (
            <div style={{ borderRadius: 16, overflow: 'hidden', background: 'var(--hj-surface-1)', aspectRatio: '1 / 1' }}>
              <img src={imgSrc(photos[shown])} alt={product.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
          )}
          {photos.length > 1 && (
            <div className="flex flex-wrap" style={{ gap: 8, marginTop: 10 }}>
              {photos.map((k, i) => (
                <button key={k} type="button" aria-label={`Photo ${i + 1}`} onClick={() => setShown(i)} style={{ width: 64, height: 64, padding: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: i === shown ? `2px solid ${INK}` : '1px solid var(--hj-hairline)', background: 'none' }}>
                  <img src={imgSrc(k)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col" style={{ flex: '1 1 340px', minWidth: 0, gap: 16 }}>
          <h1 className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>{product.title}</h1>
          <div style={{ fontSize: 24, fontWeight: 700 }}>
            {money(product.price)}
            {physical && <span style={{ fontSize: 15, fontWeight: 500, color: MUTED }}> {g.shippingCents ? `+ ${money(g.shippingCents)} shipping` : '· free shipping'}</span>}
          </div>
          {typeof product.memberPriceCents === 'number' && product.memberPriceCents < product.price && (
            <p className="m-0" style={{ fontSize: 14, color: INK }}>Hey Joe Plus members pay {money(product.memberPriceCents)}.</p>
          )}

          {hasOptions && (
            <div className="flex flex-col" style={{ gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>{g.optionName || 'Option'}</span>
              <div className="flex flex-wrap" style={{ gap: 8 }} role="radiogroup" aria-label={g.optionName || 'Option'}>
                {g.options.map((o) => {
                  const out = o.stock <= 0; const on = option === o.key;
                  return (
                    <button key={o.key} type="button" role="radio" aria-checked={on} disabled={out} onClick={() => { setOption(o.key); setQty(1); }}
                      style={{ minWidth: 52, minHeight: 44, padding: '8px 14px', borderRadius: 10, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: out ? 'not-allowed' : 'pointer',
                        background: on ? INK : '#fff', color: on ? '#fff' : INK, border: `1px solid ${on ? INK : 'var(--hj-hairline)'}`, textDecoration: out ? 'line-through' : 'none', opacity: out ? 0.5 : 1 }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!soldOut && (
            <div className="flex items-center" style={{ gap: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Quantity</span>
              <div className="inline-flex items-center" style={{ border: '1px solid var(--hj-hairline)', borderRadius: 10 }}>
                <button type="button" aria-label="Fewer" disabled={qty <= 1} onClick={() => setQty((q) => Math.max(1, q - 1))} style={{ width: 44, height: 44, border: 0, background: 'none', cursor: 'pointer', color: INK }}><Minus size={16} /></button>
                <span style={{ minWidth: 28, textAlign: 'center', fontSize: 16, fontWeight: 600 }}>{qty}</span>
                <button type="button" aria-label="More" disabled={qty >= maxQty} onClick={() => setQty((q) => Math.min(maxQty, q + 1))} style={{ width: 44, height: 44, border: 0, background: 'none', cursor: 'pointer', color: INK }}><Plus size={16} /></button>
              </div>
              {left !== null && left > 0 && left <= 5 && <span style={{ fontSize: 14, color: INK, fontWeight: 600 }}>Only {left} left</span>}
            </div>
          )}

          {!isAuthenticated && !soldOut && (
            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); if (err) setErr(null); }} placeholder="you@example.com" autoComplete="email" aria-label="Your email"
              style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid var(--hj-hairline)', fontSize: 15, fontFamily: 'inherit', background: '#fff', color: INK }} />
          )}
          <button type="button" disabled={soldOut || busy || previewOnly} onClick={buy} className="inline-flex items-center justify-center gap-2"
            style={{ minHeight: 52, borderRadius: 999, border: 0, background: soldOut ? 'var(--hj-surface-2)' : 'var(--hj-yellow)', color: 'oklch(0.18 0.006 70)', fontSize: 16, fontWeight: 700, cursor: soldOut || previewOnly ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.7 : 1 }}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : soldOut ? 'Sold out' : `Buy – ${money(product.price * qty + (physical ? (g.shippingCents || 0) : 0))}`}
          </button>
          {err && <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-pink-ink)', fontWeight: 600 }}>{err}</p>}
          <p className="m-0" style={{ fontSize: 14, color: MUTED, lineHeight: 1.45 }}>
            {physical ? 'Ships to US addresses. You’ll enter your address at checkout.' : 'A download. You get it right after you pay.'}
            {creator?.displayName ? ` Sold by ${creator.displayName}.` : ''}
          </p>
          {product.description && <div style={{ fontSize: 15.5, lineHeight: 1.6, whiteSpace: 'pre-line', borderTop: '1px solid var(--hj-hairline)', paddingTop: 16 }}>{product.description}</div>}
        </div>
      </div>
    </main>
  );
};

export default GoodsProductView;
